import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { LOT_TEMPLATE_NAME_MAX, type LotTemplate, type LotTemplatePatch } from "@omts/shared";
import { api } from "../../api";
import { fmtDateTime } from "../../time";
import { ActiveBadge, errMsg, useList } from "./common";

/** Кратко, что задано в шаблоне: «маршрут, груз, транспорт». */
export function templateSummary(t: LotTemplate): string {
  const f = t.fields;
  const parts: string[] = [];
  if (f.projectId) parts.push("проект");
  if (f.loadingAddress || f.unloadingAddress || (f.intermediateAddresses?.length ?? 0) > 0) parts.push("маршрут");
  if (f.cargoName || f.cargoDescription || f.weightKg || f.lengthM) parts.push("груз");
  if (f.vehicleTypeId) parts.push("транспорт");
  if (f.specialConditions || f.insurance) parts.push("условия");
  return parts.length ? parts.join(", ") : "поля не заполнены";
}

/** Вкладка «Шаблоны заявок» (16.5): список, создание, переименование, деактивация; поля правятся формой заявки. */
export function TemplatesTab() {
  const navigate = useNavigate();
  const { items, error, reload } = useList<LotTemplate>(api.lotTemplates);
  const [name, setName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setAddError("Введите название");
    setBusy(true);
    setAddError(null);
    try {
      const t = await api.createLotTemplate({ name: name.trim(), fields: {} });
      setName("");
      navigate(`/lots/new?template=${t.id}`);
    } catch (err) {
      setAddError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: string, p: LotTemplatePatch) => {
    setRowError(null);
    try {
      await api.updateLotTemplate(id, p);
      setEditing(null);
      reload();
    } catch (err) {
      setRowError(errMsg(err));
    }
  };

  const rows = (items ?? []).filter((r) => showInactive || r.isActive);
  const inactiveCount = (items ?? []).filter((r) => !r.isActive).length;

  return (
    <>
      <form className="toolbar" onSubmit={(e) => void add(e)}>
        <div className="row">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Название шаблона, например: Алматы → Астана, тент 20 т"
            maxLength={LOT_TEMPLATE_NAME_MAX}
            className={addError ? "invalid" : ""}
          />
          <button type="submit" className="btn primary" disabled={busy}>
            Создать и заполнить
          </button>
          {inactiveCount > 0 && (
            <label className="row hint" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              показывать неактивные ({inactiveCount})
            </label>
          )}
        </div>
        <span className="muted">{items ? `Шаблонов: ${items.length}` : ""}</span>
      </form>
      {addError && <p className="error">{addError}</p>}
      {error && <p className="error">{error}</p>}
      {rowError && <p className="error">{rowError}</p>}
      <section className="card">
        {!items ? (
          <p className="muted">Загрузка…</p>
        ) : rows.length === 0 ? (
          <p className="muted">Шаблонов пока нет. Создайте здесь или откройте заявку и нажмите «Сохранить как шаблон».</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Что задано</th>
                <th>Автор</th>
                <th>Создан</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.isActive ? "" : "inactive"}>
                  <td>
                    {editing?.id === r.id ? (
                      <form
                        className="row"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (editing.name.trim()) void patch(r.id, { name: editing.name.trim() });
                        }}
                      >
                        <input value={editing.name} maxLength={LOT_TEMPLATE_NAME_MAX} onChange={(e) => setEditing({ id: r.id, name: e.target.value })} autoFocus />
                        <button type="submit" className="btn small primary">
                          Сохранить
                        </button>
                        <button type="button" className="btn small" onClick={() => setEditing(null)}>
                          Отмена
                        </button>
                      </form>
                    ) : (
                      <b>{r.name}</b>
                    )}
                  </td>
                  <td className="muted">{templateSummary(r)}</td>
                  <td>{r.createdByName ?? "—"}</td>
                  <td className="nowrap">{fmtDateTime(r.createdAt)}</td>
                  <td>
                    <ActiveBadge active={r.isActive} />
                  </td>
                  <td>
                    <div className="actions-cell">
                      <Link to={`/lots/new?template=${r.id}`} className="btn small">
                        Поля
                      </Link>
                      {editing?.id !== r.id && (
                        <button className="btn small" onClick={() => setEditing({ id: r.id, name: r.name })}>
                          Переименовать
                        </button>
                      )}
                      <button className="btn small" onClick={() => void patch(r.id, { isActive: !r.isActive })}>
                        {r.isActive ? "Деактивировать" : "Активировать"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <p className="hint">Шаблон хранит все поля формы заявки, кроме дат погрузки и дедлайна. Неактивные шаблоны не предлагаются в форме «Новая заявка».</p>
    </>
  );
}
